import React, { useEffect, useMemo, useState } from "react";

const NOTE_TASK_TYPES = [
  { value: "pre_filing", label: "Pre-filing" },
  { value: "post_filing", label: "Post-filing" },
  { value: "341", label: "341" },
  { value: "internal", label: "Internal" },
];

const TASK_STATUS = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "complete", label: "Complete" },
  { value: "cancelled", label: "Cancelled" },
];

function formatDateTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Minimal fetch helper.
 * - Adjust getAuthToken() to match your app (localStorage key, cookies, etc.)
 * - If you use cookies/session auth, delete the Authorization header and keep credentials: "include"
 */
function getAuthToken() {
  return localStorage.getItem("accessToken"); // <-- change to your key if different
}
const API = process.env.REACT_APP_API_URL;
async function apiFetch(url, { method = "GET", body } = {}) {
  const token = getAuthToken();

  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include", // keep if you use cookies too; harmless otherwise
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  const text = await res.text();
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text || null;
  }

  if (!res.ok) {
    const msg =
      (data && (data.detail || data.error || data.message)) ||
      `Request failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export default function CaseHomeNotesTasks({ caseId, apiBase = API }) {
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [loadingNotes, setLoadingNotes] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(true);

  const [errorNotes, setErrorNotes] = useState("");
  const [errorTasks, setErrorTasks] = useState("");

  // Create note
  const [newNoteType, setNewNoteType] = useState("internal");
  const [newNoteText, setNewNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Create task
  const [newTaskType, setNewTaskType] = useState("internal");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  const [savingTask, setSavingTask] = useState(false);

  const notesUrl = `${apiBase}/cases/${caseId}/notes/`;
  const tasksUrl = `${apiBase}/cases/${caseId}/tasks/`;

  async function loadNotes() {
    setErrorNotes("");
    setLoadingNotes(true);
    try {
      const data = await apiFetch(notesUrl);
      setNotes(Array.isArray(data) ? data : []);
    } catch (e) {
      setErrorNotes(e.message || "Failed to load notes.");
    } finally {
      setLoadingNotes(false);
    }
  }

  async function loadTasks() {
    setErrorTasks("");
    setLoadingTasks(true);
    try {
      const data = await apiFetch(tasksUrl);
      setTasks(Array.isArray(data) ? data : []);
    } catch (e) {
      setErrorTasks(e.message || "Failed to load tasks.");
    } finally {
      setLoadingTasks(false);
    }
  }

  useEffect(() => {
    if (!caseId) return;
    loadNotes();
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  async function createNote(e) {
    e.preventDefault();
    const note = newNoteText.trim();
    if (!note) return;

    setSavingNote(true);
    setErrorNotes("");
    try {
      const created = await apiFetch(notesUrl, {
        method: "POST",
        body: { note_type: newNoteType, note },
      });
      setNotes((prev) => [created, ...prev]);
      setNewNoteText("");
    } catch (e2) {
      setErrorNotes(e2.message || "Failed to add note.");
    } finally {
      setSavingNote(false);
    }
  }

  async function createTask(e) {
    e.preventDefault();
    const title = newTaskTitle.trim();
    if (!title) return;

    setSavingTask(true);
    setErrorTasks("");
    try {
      const payload = {
        task_type: newTaskType,
        title,
        status: "pending",
        priority: "normal",
        ...(newTaskDue ? { due_date: newTaskDue } : {}),
      };

      const created = await apiFetch(tasksUrl, {
        method: "POST",
        body: payload,
      });

      setTasks((prev) => [created, ...prev]);
      setNewTaskTitle("");
      setNewTaskDue("");
    } catch (e2) {
      setErrorTasks(e2.message || "Failed to add task.");
    } finally {
      setSavingTask(false);
    }
  }

  async function patchTask(taskId, patch) {
    // optimistic update
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));

    try {
      const updated = await apiFetch(`${tasksUrl}${taskId}/`, {
        method: "PATCH",
        body: patch,
      });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    } catch (e) {
      setErrorTasks(e.message || "Failed to update task.");
      loadTasks(); // revert to server truth
    }
  }

  async function deleteNote(noteId) {
    // optimistic remove
    const prev = notes;
    setNotes((cur) => cur.filter((n) => n.id !== noteId));
    setErrorNotes("");

    try {
      await apiFetch(`${notesUrl}${noteId}/`, { method: "DELETE" });
    } catch (e) {
      setErrorNotes(e.message || "Failed to delete note.");
      // revert
      setNotes(prev);
    }
  }

  async function deleteTask(taskId) {
    // optimistic remove
    const prev = tasks;
    setTasks((cur) => cur.filter((t) => t.id !== taskId));
    setErrorTasks("");

    try {
      await apiFetch(`${tasksUrl}${taskId}/`, { method: "DELETE" });
    } catch (e) {
      setErrorTasks(e.message || "Failed to delete task.");
      // revert
      setTasks(prev);
    }
  }

  const openTasks = useMemo(
    () => tasks.filter((t) => t.status !== "complete" && t.status !== "cancelled"),
    [tasks]
  );
  const doneTasks = useMemo(() => tasks.filter((t) => t.status === "complete"), [tasks]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* NOTES CARD */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-gray-900 font-semibold text-lg">Notes</h2>
          <button
            type="button"
            onClick={loadNotes}
            className="text-sm text-indigo-600 hover:text-indigo-500"
            disabled={loadingNotes}
          >
            Refresh
          </button>
        </div>

        <form onSubmit={createNote} className="mb-4">
          <div className="flex gap-2 mb-2">
            <select
              value={newNoteType}
              onChange={(e) => setNewNoteType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              {NOTE_TASK_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            <button
              type="submit"
              disabled={savingNote || !newNoteText.trim()}
              className="ml-auto bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
            >
              {savingNote ? "Saving…" : "Add note"}
            </button>
          </div>

          <textarea
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            rows={3}
            placeholder="Add a case note…"
            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          {errorNotes ? <div className="text-red-600 text-sm mt-2">{errorNotes}</div> : null}
        </form>

        {loadingNotes ? (
          <div className="text-gray-600 text-sm">Loading notes…</div>
        ) : notes.length === 0 ? (
          <div className="text-gray-500 text-sm">No notes yet.</div>
        ) : (
          <div className="space-y-3 max-h-[520px] overflow-auto pr-1">
            {notes.map((n) => (
              <div key={n.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1 gap-3">
                  <div className="text-xs font-medium text-indigo-700">
                    {n.note_type_display || n.note_type}
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-xs text-gray-500">{formatDateTime(n.created_at)}</div>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm("Delete this note?")) deleteNote(n.id);
                      }}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="text-gray-900 text-sm whitespace-pre-wrap">{n.note}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* TASKS CARD */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-gray-900 font-semibold text-lg">Tasks</h2>
          <button
            type="button"
            onClick={loadTasks}
            className="text-sm text-indigo-600 hover:text-indigo-500"
            disabled={loadingTasks}
          >
            Refresh
          </button>
        </div>

        <form onSubmit={createTask} className="mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select
              value={newTaskType}
              onChange={(e) => setNewTaskType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              {NOTE_TASK_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            <input
              value={newTaskDue}
              onChange={(e) => setNewTaskDue(e.target.value)}
              type="date"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            />

            <button
              type="submit"
              disabled={savingTask || !newTaskTitle.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
            >
              {savingTask ? "Saving…" : "Add task"}
            </button>
          </div>

          <input
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            placeholder="Task title…"
            className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          {errorTasks ? <div className="text-red-600 text-sm mt-2">{errorTasks}</div> : null}
        </form>

        {loadingTasks ? (
          <div className="text-gray-600 text-sm">Loading tasks…</div>
        ) : tasks.length === 0 ? (
          <div className="text-gray-500 text-sm">No tasks yet.</div>
        ) : (
          <div className="space-y-4 max-h-[520px] overflow-auto pr-1">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                Open ({openTasks.length})
              </div>

              <div className="space-y-2">
                {openTasks.map((t) => (
                  <div key={t.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-gray-900 text-sm font-medium truncate">
                          {t.title}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          <span className="font-medium text-indigo-700">
                            {t.task_type_display || t.task_type}
                          </span>
                          {t.due_date ? (
                            <>
                              <span className="mx-2">•</span>
                              Due {t.due_date}
                            </>
                          ) : null}
                        </div>
                      </div>

                      <div className="flex gap-2 items-center">
                        <select
                          value={t.task_type}
                          onChange={(e) => patchTask(t.id, { task_type: e.target.value })}
                          className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                        >
                          {NOTE_TASK_TYPES.map((x) => (
                            <option key={x.value} value={x.value}>
                              {x.label}
                            </option>
                          ))}
                        </select>

                        <select
                          value={t.status}
                          onChange={(e) => patchTask(t.id, { status: e.target.value })}
                          className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                        >
                          {TASK_STATUS.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm("Delete this task?")) deleteTask(t.id);
                          }}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {t.description ? (
                      <div className="text-gray-700 text-sm mt-2 whitespace-pre-wrap">
                        {t.description}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            {doneTasks.length ? (
              <div>
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Completed ({doneTasks.length})
                </div>
                <div className="space-y-2">
                  {doneTasks.slice(0, 10).map((t) => (
                    <div key={t.id} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-gray-900 text-sm font-medium line-through truncate">
                            {t.title}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="font-medium text-indigo-700">
                              {t.task_type_display || t.task_type}
                            </span>
                            {t.completed_at ? (
                              <>
                                <span className="mx-2">•</span>
                                Completed {formatDateTime(t.completed_at)}
                              </>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => patchTask(t.id, { status: "pending" })}
                            className="text-xs text-indigo-600 hover:text-indigo-500"
                          >
                            Reopen
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm("Delete this task?")) deleteTask(t.id);
                            }}
                            className="text-xs text-red-600 hover:text-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {doneTasks.length > 10 ? (
                    <div className="text-xs text-gray-500">Showing 10 most recent completed.</div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}